import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bc_admin_token";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
}

const fmtDate = (d) => {
  try {
    return format(parseISO(d), "EEE dd/MM/yyyy");
  } catch {
    return d;
  }
};

const fmtTime = (t) => {
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  if (!token) {
    return <LoginCard onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  }
  return <Diary token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }} />;
}

function LoginCard({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      onLogin(data.token);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F1EA] flex items-center justify-center px-5" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm bg-[#FAF8F5] border border-[#E4DCCE] rounded-3xl p-8 space-y-5 shadow-sm">
        <div className="space-y-1 text-center">
          <span className="text-2xl font-display font-semibold text-[#1E282D]">Beneficence Care</span>
          <p className="text-xs text-[#6B7980]">Owner sign-in — Bookings Diary</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#48565D] mb-1" htmlFor="admin-email">Email</label>
          <input id="admin-email" data-testid="admin-email-input" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="you@beneficencecare.com"
            className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-[#D5CABB] bg-white focus:outline-none focus:border-[#2E3C42]" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#48565D] mb-1" htmlFor="admin-password">Password</label>
          <input id="admin-password" data-testid="admin-password-input" type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)} placeholder="Your diary password"
            className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-[#D5CABB] bg-white focus:outline-none focus:border-[#2E3C42]" />
        </div>
        {error && <p className="text-xs text-red-700" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="admin-login-button"
          className="w-full py-3 rounded-xl font-medium text-sm text-[#FAF8F5] bg-[#223036] hover:bg-[#141E22] transition-colors disabled:opacity-60">
          {loading ? "Signing in..." : "Sign In"}
        </button>
        <a href="/" className="block text-center text-xs text-[#6B7980] hover:text-[#1E282D] transition-colors">Back to website</a>
      </form>
    </div>
  );
}

function Diary({ token, onLogout }) {
  const [tab, setTab] = useState("upcoming");
  const [data, setData] = useState({ upcoming: [], past: [], enquiries: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/admin/bookings`, { headers }),
      axios.get(`${API}/admin/enquiries`, { headers }),
    ])
      .then(([b, e]) => setData({ ...b.data, enquiries: e.data.enquiries }))
      .catch((err) => {
        if (err?.response?.status === 401) {
          toast.error("Session expired — please sign in again.");
          onLogout();
        } else {
          toast.error("Could not load your diary.");
        }
      })
      .finally(() => setLoading(false));
  }, [token, onLogout]);

  const TABS = [
    { id: "upcoming", label: `Upcoming (${data.upcoming.length})` },
    { id: "past", label: `Past (${data.past.length})` },
    { id: "enquiries", label: `Enquiries (${data.enquiries.length})` },
  ];

  return (
    <div className="min-h-screen bg-[#F6F1EA] px-5 sm:px-8 py-10" data-testid="admin-diary">
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <span className="text-xs uppercase tracking-widest text-[#786655] font-semibold">Beneficence Care</span>
            <h1 className="text-2xl sm:text-3xl font-display font-medium text-[#1E282D]">Bookings Diary</h1>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-xs text-[#6B7980] hover:text-[#1E282D] transition-colors">Website</a>
            <button onClick={onLogout} data-testid="admin-logout-button"
              className="px-4 py-2 text-xs font-medium text-[#323E44] bg-[#EFE7DC] hover:bg-[#E4D9CC] rounded-full transition-colors">
              Sign Out
            </button>
          </div>
        </div>

        <div className="flex gap-2" data-testid="diary-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`diary-tab-${t.id}`}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${tab === t.id ? "bg-[#2E3C42] text-white" : "bg-[#FAF8F5] text-[#4D5A60] border border-[#E4DCCE] hover:bg-[#EFE7DC]"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-[#6B7980]">Loading your diary...</p>
        ) : tab === "enquiries" ? (
          <EnquiryList items={data.enquiries} />
        ) : (
          <BookingList items={tab === "upcoming" ? data.upcoming : data.past} empty={tab === "upcoming" ? "No upcoming appointments." : "No past appointments."} />
        )}
      </div>
    </div>
  );
}

function BookingList({ items, empty }) {
  if (!items.length) return <p className="text-sm text-[#6B7980]">{empty}</p>;
  return (
    <div className="space-y-3">
      {items.map((b) => (
        <div key={b.booking_id} data-testid={`booking-row-${b.booking_id}`} className="bg-[#FAF8F5] border border-[#E4DCCE] rounded-2xl p-5 grid grid-cols-1 sm:grid-cols-12 gap-3 items-start">
          <div className="sm:col-span-3">
            <div className="text-sm font-semibold text-[#1E282D]">{fmtDate(b.date)}</div>
            <div className="text-xs text-[#6B7980]">{fmtTime(b.time)} ACST</div>
            {b.reminder_sent && <span className="inline-block mt-1 text-[10px] font-semibold uppercase tracking-wide text-[#5C6B4E] bg-[#EAE7DC] px-2 py-0.5 rounded-full">Reminder sent</span>}
          </div>
          <div className="sm:col-span-5 space-y-0.5">
            <div className="text-sm font-medium text-[#1E282D]">{b.name}</div>
            <div className="text-xs text-[#4E5B62]">{b.service}</div>
            <div className="text-xs text-[#6B7980]">Booking for: {b.booking_for}</div>
            {b.notes && <div className="text-xs text-[#6B7980] italic">"{b.notes}"</div>}
          </div>
          <div className="sm:col-span-4 space-y-0.5 text-xs">
            <a href={`tel:${b.phone}`} className="block text-[#334046] underline">{b.phone}</a>
            {b.email && <a href={`mailto:${b.email}`} className="block text-[#334046] underline">{b.email}</a>}
          </div>
        </div>
      ))}
    </div>
  );
}

function EnquiryList({ items }) {
  if (!items.length) return <p className="text-sm text-[#6B7980]">No enquiries yet.</p>;
  return (
    <div className="space-y-3">
      {items.map((e) => (
        <div key={e.enquiry_id} data-testid={`enquiry-row-${e.enquiry_id}`} className="bg-[#FAF8F5] border border-[#E4DCCE] rounded-2xl p-5 space-y-1.5">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-semibold text-[#1E282D]">{e.name}</div>
            <div className="text-xs text-[#6B7980]">{e.created_at ? fmtDate(e.created_at.slice(0, 10)) : ""}</div>
          </div>
          <div className="text-xs text-[#334046] underline">{e.contact}</div>
          {e.services && <div className="text-xs text-[#4E5B62]">Needs: {e.services}</div>}
          <p className="text-sm text-[#4E5B62] leading-relaxed">{e.message}</p>
        </div>
      ))}
    </div>
  );
}
